import React, { useEffect, useRef } from "react";

/**
 * Fixed cinematic video background that runs behind the entire site.
 * - Plays muted, looped, autoplay
 * - Dark overlay + vignette + grain to keep content readable
 * - Pauses when tab is hidden to save resources
 */
const SiteBackground = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onVisibility = () => {
      if (document.hidden) v.pause();
      else v.play().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisibility);
    // Kick off playback (covers some autoplay edge cases)
    v.play().catch(() => {});
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  return (
    <div
      aria-hidden="true"
      data-testid="site-background"
      className="fixed inset-0 w-full h-full overflow-hidden pointer-events-none"
      style={{ zIndex: 0 }}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        src="/atmosphere.mp4"
        poster="/atmosphere-poster.jpg"
        muted
        loop
        playsInline
        autoPlay
        preload="auto"
        data-testid="site-background-video"
      />

      {/* Darkening so foreground text/imagery stays luxurious & readable */}
      <div
        className="absolute inset-0"
        style={{ background: "rgba(0,0,0,0.55)" }}
      />

      {/* Subtle vignette to focus center content */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.35) 70%, rgba(0,0,0,0.7) 100%)",
        }}
      />

      {/* Cinematic grain */}
      <div
        className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />
    </div>
  );
};

export default SiteBackground;
